import React from 'react';
import BaseStyles from './shield_icon_selector.module.css';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { Modal } from 'ui/base/modal';
import { StandardPage } from 'ui/base/page/standard_page';
import { BackButton } from 'ui/base/button/back_button';
import Messages from './shield_icon_selector_messages';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';
import { ColorCode as ColorCodeType } from 'services/accounts/types';
import { ColorCode } from 'services/accounts/protos/pangolin_dto_pb';
import { observer } from 'mobx-react';
import { ButtonClickEvent } from 'ui/base/types';
import { ShieldIconSelectorStore } from './shield_icon_selector.store';

type ShieldIconSelectorStyles = {
  modalContainer: string;
  headerContainer: string;
  ok: string;
  ShieldListContainer: string;
  iconContainer: string;
  baseShield: string;
  selectedShield: string;
};

const themedStyles: ThemedStyles<ShieldIconSelectorStyles> = {
  [Theme.DAYLIGHT]: {
    modalContainer: BaseStyles.modalContainer,
    headerContainer: BaseStyles.headerContainer,
    ok: BaseStyles.daylightOk,
    ShieldListContainer: BaseStyles.ShieldListContainer,
    iconContainer: BaseStyles.iconContainer,
    baseShield: BaseStyles.baseShield,
    selectedShield: BaseStyles.daylightSelectedShield,
  },
  [Theme.MIDNIGHT]: {
    modalContainer: BaseStyles.modalContainer,
    headerContainer: BaseStyles.headerContainer,
    ok: BaseStyles.midnightOk,
    ShieldListContainer: BaseStyles.ShieldListContainer,
    iconContainer: BaseStyles.iconContainer,
    baseShield: BaseStyles.baseShield,
    selectedShield: BaseStyles.midnightSelectedShield,
  },
};

type ShieldOptionProps = {
  store: ShieldIconSelectorStore;
  colorCode: ColorCodeType;
  highlighted: boolean;
};

const ShieldOption = observer(({
  store,
  colorCode,
  highlighted,
}: ShieldOptionProps) => {
  const styles = useStyles(themedStyles);
  const setHighlighted = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.setHighlighted(colorCode);
  }, [store, colorCode]);
  return (
    <button type='button' className={styles.iconContainer} onClick={setHighlighted}>
      <div className={highlighted ? styles.selectedShield : styles.baseShield}>
        <ShieldIcon colorCode={colorCode} size={ShieldSize.LARGE}/>
      </div>

    </button>
  );
});

type ModalContentProps = {
  store: ShieldIconSelectorStore;
};

const ShieldContentModal = observer(({ store }: ModalContentProps) => {
  const styles = useStyles(themedStyles);
  const handleOk = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.commitHighlighted();
    store.modal.hideModal();
  }, [store]);
  return (
    <div className={styles.modalContainer}>
      <StandardPage>
        <div className={styles.headerContainer}>
          <BackButton onClick={store.modal.hideModal}/>
          <button type='button' onClick={handleOk} className={styles.ok}>{Messages.ok()}</button>
        </div>
        <div className={styles.ShieldListContainer}>
          {Object.values(ColorCode).map((color: ColorCodeType) => (
            <ShieldOption
              key={color}
              store={store}
              colorCode={color}
              highlighted={color === store.highlighted}
            />
          ))}
        </div>
      </StandardPage>
    </div>
  );
});

type SelectedShieldProps = {
  store: ShieldIconSelectorStore;
};

const SelectedShield = observer(({ store }: SelectedShieldProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.reloadHighlighted();
    store.modal.showModal();
  }, [store]);
  return (
    <button type='button' onClick={onShowModal} className={styles.iconContainer}>
      <ShieldIcon colorCode={store.value} size={ShieldSize.EXTRA_LARGE}/>
    </button>
  );
});

type ShieldIconSelectorProps = {
  store: ShieldIconSelectorStore;
};

export const ShieldIconSelector = ({ store }: ShieldIconSelectorProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <ShieldContentModal store={store}/>
      </Modal>
      <SelectedShield store={store}/>
    </>
  );
};
